// PATH: src/hooks/useCountUp.ts — un nombre qui monte jusqu'à sa valeur.
//
// L'animation n'est pas décorative : sur un tableau de bord, les chiffres
// arrivent après la page (une requête les précède). Les faire monter signale
// que la valeur vient d'être mesurée, là où une apparition sèche ressemble à
// un rendu figé. Le mouvement est court et décélère — on lit la valeur finale,
// pas le trajet.
import { useEffect, useRef, useState } from "react";

/** Décélération : rapide au départ, posée à l'arrivée sur le chiffre à lire. */
const easeOut = (t: number) => 1 - Math.pow(1 - t, 3);

export const useCountUp = (value: number, duration = 650): number => {
  const [shown, setShown] = useState(value);
  const from = useRef(value);
  const frame = useRef<number>();

  useEffect(() => {
    // Préférence système : le nombre est posé directement, sans trajet.
    const reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    const start = from.current;
    if (reduced || start === value) {
      from.current = value;
      setShown(value);
      return;
    }

    const t0 = performance.now();
    const step = (now: number) => {
      const p = Math.min(1, (now - t0) / duration);
      setShown(start + (value - start) * easeOut(p));
      if (p < 1) frame.current = requestAnimationFrame(step);
      else from.current = value;
    };
    frame.current = requestAnimationFrame(step);
    return () => {
      if (frame.current) cancelAnimationFrame(frame.current);
      // La valeur atteinte devient le point de départ du prochain trajet :
      // une animation interrompue ne doit pas repartir de l'ancien chiffre.
      from.current = value;
    };
  }, [value, duration]);

  return shown;
};
